import React, { useState, useEffect } from 'react';

import { Modal, Divider, Table, Tooltip, notification, Flex } from 'antd';
import { ColumnsType } from 'antd/es/table';
import {
    DeleteOutlined,
    EditOutlined,
    PlusOutlined,
    LoadingOutlined,
    QuestionCircleOutlined,
    ReloadOutlined,
    CheckOutlined,
} from '@ant-design/icons';

import Employee from '@components/users/employee';
import IBaseComponentProps from '@components/base-component-props';

import Toolbar from '@controls/toolbar/toolbar';

import { exception } from '@extensions/notification';

import { userLoaded } from '@store/actions';
import { useAppDispatch } from '@store/hooks';

import { IUser } from '@entities/user';

import { UserType } from '@enums/user-type';

import { EmailIcon, MobileIcon, MobileNoneIcon, TelegramIcon } from '@icons/index';

interface IEmployeesProps extends IBaseComponentProps {
    userId: string;
    userLogin?: string;
    userType: UserType | undefined;
    isViewOnly: boolean;
}

const Employees = (props: IEmployeesProps) => {
    const { userId, userLogin, userType, isViewOnly, serverFetch } = props;

    const [modal, modalContextHolder] = Modal.useModal();

    const d = useAppDispatch();
    const [api, contextHolder] = notification.useNotification();

    const [employees, setEmployees] = useState<Array<IUser>>([]);
    const [openEmployee, setOpenEmployee] = useState<boolean>(false);
    const [currentEmployee, setCurrentEmployee] = useState<IUser>();
    const [selectedEmployeeIds, setEmployeeSelectedIds] = useState<React.Key[]>([]);
    const [refreshRequired, setRefreshRequired] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        if (!refreshRequired) return;

        setEmployees([]);

        setEmployeeSelectedIds([]);
        setCurrentEmployee(undefined);

        setRefreshRequired(false);

        setLoading(true);

        serverFetch(`users/${userId}/employees`, { method: 'GET' })
            .then((data: any) => {
                setEmployees(data);
                setLoading(false);
            })
            .catch((ex: any) => {
                setLoading(false);
                exception(api, 'Ошибка получения сотрудников', ex, () => d(userLoaded(undefined)));
            });
    }, [refreshRequired]);

    const onDeleteEmployee = () => {
        serverFetch(`users/${currentEmployee?.id}`, { method: 'DELETE' })
            .then(() => {
                setRefreshRequired(true);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка удаления сотрудника', ex, () => d(userLoaded(undefined)));
            });
    };

    const onEmployeeSelectChange = (selectedRowKeys: React.Key[]) => {
        setEmployeeSelectedIds(selectedRowKeys);

        if (selectedRowKeys.length == 1) {
            const entity = employees.find((c) => c.id === selectedRowKeys[0]);
            setCurrentEmployee(entity);
        } else {
            setCurrentEmployee(undefined);
        }
    };

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        disabled: loading || loading || !userId,
                        key: 'refresh',
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setRefreshRequired(true);
                        },
                    },
                    {
                        label: 'Добавить ',
                        key: 'add',
                        type: 'primary',
                        icon: <PlusOutlined />,
                        disabled: isViewOnly,
                        onClick: () => {
                            setOpenEmployee(true);
                        },
                    },
                    {
                        label: 'Изменить',
                        key: 'edit',
                        disabled: !currentEmployee || isViewOnly,
                        icon: <EditOutlined />,
                        onClick: () => {
                            setOpenEmployee(true);
                        },
                    },
                    {
                        label: 'Удалить',
                        key: 'delete',
                        disabled: !currentEmployee || isViewOnly,
                        icon: <DeleteOutlined />,
                        onClick: () => {
                            modal.confirm({
                                title: `Удалить сотрудника "${currentEmployee?.login}"?`,
                                icon: <QuestionCircleOutlined />,
                                okType: 'primary',
                                okText: 'Удалить',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    onDeleteEmployee();
                                },
                            });
                        },
                    },
                ]}
            />
        );
    };

    const renderEmployees = () => {
        const columns: ColumnsType<IUser> = [
            {
                title: 'Логин',
                dataIndex: 'login',
                width: 200,
            },
            {
                title: 'Разрешения',
                width: 300,
                align: 'left',
                render: (_, record) => {
                    return (
                        record.userSettings && (
                            <ul>
                                {record.userSettings.showOrders && <li>Отображать заказы</li>}
                                {record.userSettings.showBills && <li>Отображать счета</li>}
                                {record.userSettings.showBalances && <li>Отображать балансы</li>}
                                {record.userSettings.showInventory && <li>Отображать складские остатки</li>}
                                {record.userSettings.showTransactions && <li>Отображать историю операций</li>}
                                {record.userSettings.showDocuments && <li>Отображать документы</li>}
                            </ul>
                        )
                    );
                },
            },
            {
                title: 'Активно',
                dataIndex: 'isActive',
                width: 80,
                align: 'center',
                render: (_: any, record: IUser) => {
                    return record.isActive ? <CheckOutlined /> : '';
                },
            },
            {
                title: 'ФИО',
                dataIndex: 'fullName',
                width: 300,
            },
            {
                title: 'Телефон',
                dataIndex: 'phone',
                width: 150,
            },
            {
                width: 100,
                align: 'center',
                render: (_, record) => {
                    return (
                        <Flex gap='small'>
                            <Tooltip
                                title={`Приложение "My Freshline" ${record.hasMobileApp ? 'используется' : 'не используется'}.`}
                                color={record.hasMobileApp ? 'var(--main-green)' : 'var(--main-red)'}
                            >
                                <div>
                                    <MobileIcon
                                        style={{ color: record.hasMobileApp ? 'var(--main-green)' : 'var(--main-red)', fontSize: 20 }}
                                    />
                                </div>
                            </Tooltip>
                            <Tooltip
                                title={`Email рассылка ${record.hasEmail ? 'активна' : 'не активна'}.`}
                                color={record.hasEmail ? 'var(--main-green)' : 'var(--main-red)'}
                            >
                                <div>
                                    <EmailIcon style={{ color: record.hasEmail ? 'var(--main-green)' : 'var(--main-red)', fontSize: 20 }} />
                                </div>
                            </Tooltip>
                            <Tooltip
                                title={`Telegram Bot ${record.hasTelegram ? 'активен' : 'не активен'}.`}
                                color={record.hasTelegram ? 'var(--main-green)' : 'var(--main-red)'}
                            >
                                <div>
                                    <TelegramIcon
                                        style={{ color: record.hasTelegram ? 'var(--main-green)' : 'var(--main-red)', fontSize: 20 }}
                                    />
                                </div>
                            </Tooltip>
                            {/*   <Tooltip
                                title={`Max Bot ${record.hasMax ? 'активен' : 'не активен'}.`}
                                color={record.hasMax ? 'var(--main-green)' : 'var(--main-red)'}
                            >
                                <div>
                                    <MaxAppIcon style={{ color: record.hasMax ? 'var(--main-green)' : 'var(--main-red)', fontSize: 20 }} />
                                </div>
                            </Tooltip> */}
                        </Flex>
                    );
                },
            },
            {
                title: 'Email',
                dataIndex: 'email',
                width: 300,
            },
            {},
        ];

        return (
            <div>
                <Divider titlePlacement='left'>Сотрудники</Divider>

                {!isViewOnly && renderToolbar()}

                <Table
                    rowKey='id'
                    size='small'
                    columns={columns}
                    dataSource={employees}
                    loading={{
                        spinning: loading,
                        indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
                    }}
                    rowSelection={
                        isViewOnly
                            ? undefined
                            : {
                                  selectedRowKeys: selectedEmployeeIds,
                                  onChange: onEmployeeSelectChange,
                                  columnWidth: 32,
                                  type: 'radio',
                              }
                    }
                    onRow={(record) => {
                        return {
                            onClick: (event) => {
                                onEmployeeSelectChange([record.id || '']);
                            },
                        };
                    }}
                    pagination={false}
                    scroll={{ y: `calc(100vh - 380px)`, x: 'max-content' }}
                />
            </div>
        );
    };

    return (
        <>
            {renderEmployees()}

            {openEmployee && (
                <Employee
                    id={currentEmployee?.id}
                    parentUserId={userId}
                    parentUserLogin={userLogin}
                    parentUserType={userType}
                    onClose={(refreshRequired: boolean) => {
                        setRefreshRequired(refreshRequired);
                        setEmployeeSelectedIds([]);
                        setCurrentEmployee(undefined);
                        setOpenEmployee(false);
                    }}
                    serverFetch={serverFetch}
                />
            )}

            {contextHolder}
            {modalContextHolder}
        </>
    );
};

export default Employees;
